import { Form, FormCancel } from "../components";
import { useFeedback } from "../hooks/useFeedback";
import PropTypes from "prop-types";
import Icon from "../components/Icon";
import { Toast, PageTitle, Card, FormSection, Btn, ConfirmModal, Input, Select, Modal, Spinner, RegiaoBadge, Pagination } from "../components";
import { useState, useEffect, useRef } from "react";
import { apiFetch } from "../api";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { useTenantGeography } from "../hooks/useTenantGeography";
import { recordFormErrors } from "../observability";
import AddressAutocomplete from "./AddressAutocomplete";

function formatPhone(digits) {
  const d = (digits || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d ? `(${d}` : "";
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

function phoneToDisplay(stored) {
  const d = (stored || "").replace(/\D/g, "");
  const local = d.startsWith("55") ? d.slice(2) : d;
  return formatPhone(local);
}

function phoneToStore(displayValue) {
  const d = (displayValue || "").replace(/\D/g, "").slice(0, 11);
  return d ? `55${d}` : "";
}

function normalizeName(str) {
  return (str || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}

export default function FuncionariosPage({ pessoas, setPessoas, userRole }) {
  const isSuperadmin = userRole === "superadmin";
  const [form, setForm] = useState({ nome: "", rua: "", numero: "", bairro: "", cidade: "", telefone: "", telefone_secundario: "", regiao: "", empresa_destino_id: "", cliente_id: "" });
  const [showSecondary, setShowSecondary] = useState(false);
  const { regiao: REGIAO, regioes_bairros: REGIOES_BAIRROS, cidades: CIDADES, cidadeTemRegiao, getRegiaoPorBairro } = useTenantGeography(
    isSuperadmin ? (form.cliente_id || null) : null
  );
  const [modal, setModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const { toast, toastType, showToast, showError, dismissToast } = useFeedback("funcionarios");
  const [confirmId, setConfirmId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [empresasDestino, setEmpresasDestino] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [nomeVerificado, setNomeVerificado] = useState(false);
  const [nomeDuplicado, setNomeDuplicado] = useState(false);
  const nomeCheckTimer = useRef(null);

  const resolveRegiao = (pessoa) => (
    pessoa?.regiao || getRegiaoPorBairro(pessoa?.bairro, pessoa?.cidade) || ""
  );

  useEffect(() => {
    const promises = [apiFetch("/empresas-destino")];
    if (isSuperadmin) promises.push(apiFetch("/clientes-rotaflex"));

    Promise.all(promises)
      .then(([empresas, clientesRes = []]) => {
        setEmpresasDestino(empresas);
        setClientes(clientesRes);
      })
      .catch(() => { });
  }, [isSuperadmin]);
  const [page, setPage] = useState(1);
  const PER_PAGE = 50;

  const isMobile = useMediaQuery("(max-width: 767px)");
  const isCompact = useMediaQuery("(max-width: 1100px)");



  const filtered = pessoas
    .filter(p =>
      normalizeName(p.nome).includes(normalizeName(search)) ||
      normalizeName(p.bairro).includes(normalizeName(search))
    )
    .sort((a, b) => normalizeName(a.nome).localeCompare(normalizeName(b.nome), "pt-BR"));

  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const handleSearch = (e) => { setSearch(e.target.value); setPage(1); };

  const openNew = () => {
    setSubmitError("");
    const defaultDestino = empresasDestino.length === 1 ? empresasDestino[0].id : "";
    const f = { nome: "", rua: "", numero: "", bairro: "", cidade: "", telefone: "", telefone_secundario: "", regiao: "", empresa_destino_id: defaultDestino, cliente_id: "" };
    setForm(f); setEditItem(null); setShowSecondary(false); setNomeVerificado(false); setNomeDuplicado(false); setFormErrors({}); setModal(true);
  };

  const openEdit = (p) => {
    setSubmitError("");
    const regiaoResolvida = cidadeTemRegiao(p.cidade)
      ? (p.regiao || getRegiaoPorBairro(p.bairro, p.cidade) || "")
      : "";
    const f = {
      ...p,
      telefone_secundario: p.telefone_secundario || "",
      regiao: regiaoResolvida,
      empresa_destino_id: p.empresa_destino_id || "",
      cliente_id: p.cliente_id || "",
    };
    setForm(f); setEditItem(p.id); setShowSecondary(Boolean(p.telefone_secundario)); setNomeVerificado(true); setNomeDuplicado(false); setFormErrors({}); setModal(true);
  };

  const handleNomeChange = (e) => {
    const newNome = e.target.value;
    setForm(prev => ({ ...prev, nome: newNome }));
    if (editItem) return;
    setNomeVerificado(false);
    setNomeDuplicado(false);
    clearTimeout(nomeCheckTimer.current);
    if (!newNome.trim()) return;
    nomeCheckTimer.current = setTimeout(() => {
      const existe = pessoas.some(p => normalizeName(p.nome) === normalizeName(newNome));
      setNomeDuplicado(existe);
      setNomeVerificado(!existe);
    }, 400);
  };



  // ✅ Ao selecionar sugestão: preenche todos os campos automaticamente
  const handleAddressSelect = (detalhes) => {
    const cidadeEncontrada = CIDADES.includes(detalhes.cidade) ? detalhes.cidade : "";

    setForm(prev => {
      const cidadeFinal = cidadeEncontrada || prev.cidade;
      const regiaoEncontrada = getRegiaoPorBairro(detalhes.bairro, cidadeFinal);

      return {
        ...prev,
        rua: detalhes.rua || prev.rua,
        numero: detalhes.numero || prev.numero,
        bairro: detalhes.bairro || prev.bairro,
        cidade: cidadeFinal,
        regiao: cidadeTemRegiao(cidadeFinal) ? (regiaoEncontrada || prev.regiao || "") : "",
      };
    });
  };

  const save = async () => {
    if (loading) return;
    setSubmitError("");
    const errors = {};
    if (!form.nome) errors.nome = "Nome é obrigatório.";
    if (!form.rua) errors.rua = "Rua é obrigatória.";
    if (isSuperadmin && !form.cliente_id) errors.cliente_id = "Selecione o cliente.";
    if (Object.keys(errors).length) {
      recordFormErrors(errors, "funcionarios");
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    setLoading(true);
    try {
      const body = { ...form, regiao: cidadeTemRegiao(form.cidade) ? form.regiao : "" };
      if (editItem) {
        const res = await apiFetch(`/funcionarios/${editItem}`, { method: "PUT", body: JSON.stringify(body) });
        setPessoas(pessoas.map(p => p.id === editItem ? attachEmpresaNome(res) : p));
      } else {
        const res = await apiFetch("/funcionarios", { method: "POST", body: JSON.stringify(body) });
        setPessoas([...pessoas, attachEmpresaNome(res)]);
      }
      setModal(false);
      showToast(editItem ? "Funcionário atualizado." : "Funcionário cadastrado.");
    } catch (e) { setSubmitError(e.message); showError(e.message); }
    finally { setLoading(false); }
  };

  const empresaNome = (person) => (
    person.empresa_destino_nome
    || empresasDestino.find((empresa) => String(empresa.id) === String(person.empresa_destino_id))?.nome
    || "—"
  );

  const attachEmpresaNome = (person) => ({
    ...person,
    empresa_destino_nome: empresaNome(person),
  });

  const empresasDestinoDisponiveis = isSuperadmin && form.cliente_id
    ? empresasDestino.filter(ed => String(ed.cliente_id || "") === String(form.cliente_id))
    : empresasDestino;

  const remove = async () => {
    try {
      await apiFetch(`/funcionarios/${confirmId}`, { method: "DELETE" });
      setPessoas(pessoas.filter(p => p.id !== confirmId));
      setConfirmId(null);
      showToast("Funcionário excluído.");
    } catch (e) { showError(e.message); }
  };

  return (
    <div className="fade-in">
      {/* Header: empilha no mobile */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 24 }}>
        <div>
          <PageTitle icon="people">Funcionários</PageTitle>
          <p style={{ color: "var(--muted)", fontSize: '1rem', marginTop: 2 }}>{pessoas.length} funcionário{pessoas.length === 1 ? "" : "s"} cadastrado{pessoas.length === 1 ? "" : "s"}</p>
        </div>
        <Btn onClick={openNew}>+ Novo funcionário</Btn>
      </div>

      <Card surface="filter" style={{ marginBottom: 20 }}>
        <Input label="Buscar funcionário" type="search" placeholder="Nome ou bairro" value={search} onChange={handleSearch} />
      </Card>

      {/* Mobile: cards com telefone clicável | Desktop: tabela */}
      {isCompact ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filtered.length === 0 && (
            <Card style={{ textAlign: "center", padding: 40 }}>
              <div style={{ color: "var(--muted)", marginBottom: 14 }}>
                {search ? `Nenhum resultado para "${search}"` : "Nenhum funcionário cadastrado"}
              </div>
              {search
                ? <Btn size="sm" variant="secondary" onClick={() => { setSearch(""); setPage(1); }}>Limpar busca</Btn>
                : <Btn size="sm" onClick={openNew}>+ Cadastrar funcionário</Btn>
              }
            </Card>
          )}
          {paginated.map(p => (
            <Card key={p.id} style={{ padding: "14px 16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '1rem' }}>{p.nome}</div>
                  <div style={{ fontSize: '0.9375rem', color: "var(--muted)", marginTop: 3 }}>
                    {[p.bairro, p.cidade].filter(Boolean).join(" · ")}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: "var(--accent)", marginTop: 4, fontWeight: 600 }}>
                    <Icon name="building" /> {empresaNome(p)}
                  </div>
                  {p.telefone && (
                    <a
                      href={`tel:${p.telefone}`}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 8, fontSize: '1rem', color: "var(--accent)", fontFamily: "var(--font-mono)", textDecoration: "none" }}
                    >
                      <Icon name="phone" /> {phoneToDisplay(p.telefone)}
                    </a>
                  )}
                  {p.telefone_secundario && (
                    <a
                      href={`tel:${p.telefone_secundario}`}
                      style={{ display: "block", marginTop: 3, fontSize: '0.875rem', color: "var(--muted)", fontFamily: "var(--font-mono)", textDecoration: "none" }}
                    >
                      <Icon name="phone" /> {phoneToDisplay(p.telefone_secundario)} <span style={{ fontFamily: "inherit" }}>(secundário)</span>
                    </a>
                  )}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end", flexShrink: 0 }}>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Btn size="sm" variant="secondary" onClick={() => openEdit(p)}>Editar</Btn>
                    <Btn size="sm" variant="danger" onClick={() => setConfirmId(p.id)}>Excluir</Btn>
                  </div>
                  {cidadeTemRegiao(p.cidade) && resolveRegiao(p) && <RegiaoBadge regiao={resolveRegiao(p)} />}
                </div>
              </div>
            </Card>
          ))}
          {filtered.length > PER_PAGE && (
            <Card style={{ padding: 0 }}>
              <Pagination page={page} total={filtered.length} perPage={PER_PAGE} onChange={setPage} />
            </Card>
          )}
        </div>
      ) : (
        <Card role="region" aria-label="Tabela de funcionários" tabIndex={0} style={{ padding: 0, overflowX: "auto" }}>
          <table className="rf-employee-table" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border)" }}>
                <th style={{ padding: "12px 16px", textAlign: "left", fontSize: '0.875rem', color: "var(--muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Nome</th>
                <th style={{ padding: "12px 16px", textAlign: "left", fontSize: '0.875rem', color: "var(--muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Endereço</th>
                <th style={{ padding: "12px 16px", textAlign: "left", fontSize: '0.875rem', color: "var(--muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Bairro</th>
                <th style={{ padding: "12px 16px", textAlign: "left", fontSize: '0.875rem', color: "var(--muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Cidade</th>
                <th style={{ padding: "12px 16px", textAlign: "left", fontSize: '0.875rem', color: "var(--muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Região</th>
                <th style={{ padding: "12px 16px", textAlign: "left", fontSize: '0.875rem', color: "var(--muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Empresa</th>
                <th style={{ padding: "12px 16px", textAlign: "left", fontSize: '0.875rem', color: "var(--muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Telefone</th>
                <th style={{ padding: "12px 16px" }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ padding: 40, textAlign: "center" }}>
                    <div style={{ color: "var(--muted)", marginBottom: 12 }}>
                      {search ? `Nenhum resultado para "${search}"` : "Nenhum funcionário cadastrado"}
                    </div>
                    {search
                      ? <Btn size="sm" variant="secondary" onClick={() => { setSearch(""); setPage(1); }}>Limpar busca</Btn>
                      : <Btn size="sm" onClick={openNew}>+ Cadastrar funcionário</Btn>
                    }
                  </td>
                </tr>
              )}
              {paginated.map((p, i) => (
                <tr key={p.id}
                  style={{ borderBottom: i < paginated.length - 1 ? "1px solid var(--border)" : "none", transition: "background 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--s2)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <td style={{ padding: "12px 16px", fontWeight: 500 }}>{p.nome}</td>
                  <td style={{ padding: "12px 16px", color: "var(--muted)", fontSize: '0.9375rem' }}>{p.rua}{p.numero ? `, ${p.numero}` : ""}</td>
                  <td style={{ padding: "12px 16px", color: "var(--muted)", fontSize: '0.9375rem' }}>{p.bairro}</td>
                  <td style={{ padding: "12px 16px", color: "var(--muted)", fontSize: '0.9375rem' }}>{p.cidade}</td>
                  <td style={{ padding: "12px 16px" }}>{cidadeTemRegiao(p.cidade) && resolveRegiao(p) && <RegiaoBadge regiao={resolveRegiao(p)} />}</td>
                  <td style={{ padding: "12px 16px", color: "var(--muted)", fontSize: '0.9375rem' }}>{empresaNome(p)}</td>
                  <td style={{ padding: "12px 16px", color: "var(--muted)", fontSize: '0.9375rem', fontFamily: "var(--font-mono)" }}>
                    {p.telefone
                      ? <a href={`tel:${p.telefone}`} style={{ color: "var(--muted)", textDecoration: "none" }}>{phoneToDisplay(p.telefone)}</a>
                      : "—"}
                    {p.telefone_secundario && (
                      <a href={`tel:${p.telefone_secundario}`} style={{ display: "block", marginTop: 2, fontSize: '0.8125rem', opacity: 0.7, color: "var(--muted)", textDecoration: "none" }}>
                        {phoneToDisplay(p.telefone_secundario)}
                      </a>
                    )}
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <Btn size="sm" variant="secondary" onClick={() => openEdit(p)}>Editar</Btn>
                      <Btn size="sm" variant="danger" onClick={() => setConfirmId(p.id)}>Excluir</Btn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={page} total={filtered.length} perPage={PER_PAGE} onChange={setPage} />
        </Card>
      )}

      {modal && (
        <Modal title={editItem ? "Editar funcionário" : "Novo funcionário"} onClose={() => !loading && setModal(false)}>
          <Form values={form} onSubmit={save} busy={loading} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <p style={{ color: "var(--muted)", fontSize: '0.875rem' }}>Campos com * são obrigatórios.</p>
            <FormSection title="Identificação">
            <Input
              label="Nome completo *"
              value={form.nome}
              onChange={e => { handleNomeChange(e); if (formErrors.nome) setFormErrors(p => ({ ...p, nome: "" })); }}
              placeholder="João da Silva"
              error={formErrors.nome}
            />

            {nomeDuplicado && (
              <div style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.4)", borderRadius: 8, padding: "10px 14px", fontSize: '0.9375rem', color: "#ef4444", display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: '1rem' }}><Icon name="warning" /></span>
                <span>Já existe um funcionário cadastrado com esse nome.</span>
              </div>
            )}

                {isSuperadmin && (nomeVerificado || editItem) && (
                  <Select
                    label="Cliente *"
                    value={form.cliente_id}
                    onChange={e => { setForm({ ...form, cliente_id: e.target.value, empresa_destino_id: "" }); if (formErrors.cliente_id) setFormErrors(p => ({ ...p, cliente_id: "" })); }}
                    error={formErrors.cliente_id}
                  >
                    <option value="">Selecione o cliente</option>
                    {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </Select>
                )}

                </FormSection>
                {!editItem && !nomeVerificado && !nomeDuplicado && <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
                  Informe o nome completo. Vamos verificar se o funcionário já está cadastrado antes de continuar.
                </p>}
                {(nomeVerificado || editItem) && <>
                <FormSection title="Contato">
                <Input
                  label="Telefone principal (WhatsApp)" type="tel" inputMode="tel"
                  data-testid="func-telefone"
                  value={phoneToDisplay(form.telefone)}
                  onChange={e => setForm({ ...form, telefone: phoneToStore(e.target.value) })}
                  placeholder="(16) 99999-0000"
                />
                <span style={{ fontSize: '0.8125rem', color: "var(--muted)", marginTop: -8, display: "block" }}>
                  Informe DDD e número, sem +55. Usado para avisos da rota.
                </span>

                {!showSecondary ? (
                  <Btn
                    size="sm"
                    variant="secondary"
                    onClick={() => setShowSecondary(true)}
                    style={{ marginTop: -2, alignSelf: "flex-start" }}
                  >
                    <Icon name="plus" /> Adicionar telefone
                  </Btn>
                ) : (
                  <>
                    <Input
                      label="Telefone alternativo" type="tel" inputMode="tel"
                      data-testid="func-telefone-secundario"
                      value={phoneToDisplay(form.telefone_secundario)}
                      onChange={e => setForm({ ...form, telefone_secundario: phoneToStore(e.target.value) })}
                      placeholder="(16) 99999-0000"
                    />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: -8 }}>
                      <span style={{ fontSize: '0.8125rem', color: "var(--muted)" }}>
                        <Icon name="warning" /> Se o principal falhar no WhatsApp, tentamos este.
                      </span>
                      <Btn size="sm" variant="secondary" onClick={() => { setShowSecondary(false); setForm({ ...form, telefone_secundario: "" }); }}>Remover telefone alternativo</Btn>
                    </div>
                  </>
                )}
                </FormSection>
                <FormSection title="Endereço de embarque">
                {/* Rua com autocomplete + Número na mesma linha no desktop; empilhados no mobile */}
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr auto", gap: 12, alignItems: "end" }}>
                  <AddressAutocomplete
                    label="Rua *"
                    value={form.rua}
                    onChange={val => { setForm({ ...form, rua: val }); if (formErrors.rua) setFormErrors(p => ({ ...p, rua: "" })); }}
                    onSelect={handleAddressSelect}
                    placeholder="Digite a rua para buscar..."
                    error={formErrors.rua}
                  />
                  <Input
                    label="Número"
                    value={form.numero}
                    onChange={e => setForm({ ...form, numero: e.target.value })}
                    placeholder="100"
                    style={{ width: isMobile ? "100%" : 80 }}
                  />
                </div>

                <Input
                  label="Bairro"
                  value={form.bairro}
                  onChange={e => setForm({ ...form, bairro: e.target.value })}
                  placeholder="Centro"
                />

                <Select label="Cidade" value={form.cidade} onChange={e => setForm({ ...form, cidade: e.target.value, regiao: "" })}>
                  <option value="">Selecione a cidade</option>
                  {CIDADES.map(z => <option key={z} value={z}>{z}</option>)}
                </Select>
                {CIDADES.length === 0 && <p style={{ color: "var(--muted)", fontSize: '0.875rem' }}>Nenhuma cidade configurada. Peça ao administrador para revisar as cidades atendidas.</p>}

                {cidadeTemRegiao(form.cidade) && (
                  <>
                    <Select label="Região" value={form.regiao} onChange={e => setForm({ ...form, regiao: e.target.value })}>
                      <option value="">Selecione a região</option>
                      {REGIAO.map(r => <option key={r} value={r}>{r}</option>)}
                    </Select>
                    {form.regiao && REGIOES_BAIRROS[form.regiao] && (
                      <details>
                        <summary className="rf-inline-disclosure">
                          <Icon name="chevron-down" size={16} /> Ver bairros desta região
                        </summary>
                        <p style={{ marginTop: 12, fontSize: '0.875rem', color: "var(--muted)", lineHeight: 1.6 }}>
                          {REGIOES_BAIRROS[form.regiao].join(", ")}
                        </p>
                      </details>
                    )}
                  </>
                )}

                </FormSection>
                <FormSection title="Destino habitual">
                <p style={{ color: "var(--muted)", fontSize: '0.875rem' }}>Empresa sugerida ao incluir este funcionário em uma rota.</p>
                {empresasDestinoDisponiveis.length === 0 && <p style={{ color: "var(--muted)", fontSize: '0.875rem' }}>{isSuperadmin && !form.cliente_id ? "Selecione o cliente para ver seus destinos." : "Nenhum destino disponível. Peça ao administrador para cadastrar em Empresas de destino; você pode salvar sem este vínculo."}</p>}
                {empresasDestinoDisponiveis.length > 0 && (
                  <Select
                    label="Empresa destino"
                    value={form.empresa_destino_id}
                    onChange={e => setForm({ ...form, empresa_destino_id: e.target.value })}
                  >
                    <option value="">— Selecione —</option>
                    {empresasDestinoDisponiveis.map(ed => (
                      <option key={ed.id} value={ed.id}>{ed.nome}</option>
                    ))}
                  </Select>
                )}

            </FormSection>
                </>}
            {submitError && <p role="alert" style={{ color: "var(--danger)", fontSize: '0.9375rem', lineHeight: 1.5 }}>{submitError}</p>}
            <div className="rf-form-actions">
              <FormCancel disabled={loading} />
              <Btn type="submit"  disabled={loading || (!nomeVerificado && !editItem)}>{loading ? <><Spinner /> Salvando...</> : "Salvar funcionário"}</Btn>
            </div>
          </Form>
        </Modal>
      )}

      <ConfirmModal
        open={Boolean(confirmId)}
        title="Excluir funcionário"
        message="Tem certeza? Esta ação não pode ser desfeita."
        confirmLabel="Excluir"
        confirmVariant="danger"
        onClose={() => setConfirmId(null)}
        onConfirm={remove}
      />



      {toast && !(modal && submitError) && (
        <Toast onDismiss={dismissToast} tone={toastType === "error" ? "error" : "success"}>{toast}</Toast>
      )}
    </div>
  );
}

FuncionariosPage.propTypes = {
  pessoas: PropTypes.array.isRequired, setPessoas: PropTypes.func.isRequired, userRole: PropTypes.string,
};
